import {
  ExamCenterProctorRequestStep,
  useGetExamCenterProctorRequestByIdQuery,
} from '@exam-center/graphql';
import { Navigate } from '@exam-center/router';
import { Loading } from '@shared/components';
import { createContext, useMemo } from 'react';
import { Outlet } from 'react-router-dom';

import type {
  ProctorRequestDetailsContextValue,
  ProctorRequestDetailsProviderProps,
} from './types';

const ProctorRequestDetailsContext = createContext<ProctorRequestDetailsContextValue | undefined>(
  undefined,
);

export default function ProctorRequestDetailsProvider(
  props: Readonly<ProctorRequestDetailsProviderProps>,
) {
  const { examCenterCode, proctorRequestId, children } = props;

  const { data: getProctorRequestByIdResult, loading: isUseGetProctorRequestByIdLoading } =
    useGetExamCenterProctorRequestByIdQuery({
      variables: {
        getExamCenterProctorRequestByIdInput: {
          id: proctorRequestId,
        },
      },
      fetchPolicy: 'network-only',
    });

  const proctorRequest =
    getProctorRequestByIdResult?.ecGetExamCenterProctorRequestById?.examCenterProctorRequest;

  const contextValue: ProctorRequestDetailsContextValue = useMemo(() => {
    return {
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      proctorRequest: proctorRequest!,
    };
  }, [proctorRequest]);

  if (isUseGetProctorRequestByIdLoading) {
    return <Loading sx={{ height: '100dvh' }} />;
  }

  if (!proctorRequest) {
    return (
      <Navigate
        to='/:examCenterCode/exam-proctors/requests'
        params={{
          examCenterCode,
        }}
      />
    );
  }

  if (proctorRequest.step === ExamCenterProctorRequestStep.Approved) {
    return (
      <Navigate
        to='/:examCenterCode/exam-proctors'
        params={{
          examCenterCode,
        }}
      />
    );
  }
  return (
    <ProctorRequestDetailsContext.Provider value={contextValue}>
      {children ?? <Outlet />}
    </ProctorRequestDetailsContext.Provider>
  );
}

export { ProctorRequestDetailsContext };
